/**
 * Utility functions for transforming chat history API responses to ChatMessage format
 */

import type {
  ChatWithDocumentResponse,
  ExtractedValueItemDTO,
  ExtractionResultItem,
  GetChatHistoryResponse,
  RagSourceReference,
} from '../../../../../api/document-rag/document-rag.api.types';
import type { ChatMessage } from '../../_contexts/DocumentRagContext';

/**
 * Parsed assistant message content structure from API
 * Contains detailed extraction data similar to extract endpoint
 */
interface ParsedAssistantContent {
  /** Array of extraction results */
  results?: ExtractedValueItemDTO[];
  /** Recommended extraction value */
  recommended_value?: ExtractedValueItemDTO;
}

/**
 * Build extraction results array from recommended_value and results
 * Deduplicates by id field, putting recommended_value first
 *
 * @param recommendedValue - Recommended extraction value (optional)
 * @param results - Array of extraction results (optional)
 * @param fieldName - Field name to add to each extraction result
 * @returns Array of ExtractionResultItem with deduplication applied
 */
export function buildExtractionResults(
  recommendedValue: ExtractedValueItemDTO | undefined,
  results: ExtractedValueItemDTO[] | undefined,
  fieldName: string
): ExtractionResultItem[] {
  const extractionResults: ExtractionResultItem[] = [];

  // Add recommended_value first if it exists
  if (recommendedValue) {
    extractionResults.push({
      ...recommendedValue,
      field: fieldName,
    });
  }

  // Add all results, skipping duplicates of recommended_value
  if (results) {
    for (const result of results) {
      // Skip if this result is the same as recommended_value (same id)
      const isDuplicate = result.id && result.id === recommendedValue?.id;

      if (!isDuplicate) {
        extractionResults.push({
          ...result,
          field: fieldName,
        });
      }
    }
  }

  return extractionResults;
}

/**
 * Transform chat history response to array of ChatMessage objects
 * Pairs user and assistant messages together
 *
 * @param response - Chat history response from API
 * @returns Array of ChatMessage objects
 */
export function transformChatHistoryResponse(
  response: GetChatHistoryResponse
): ChatMessage[] {
  if (
    !response.success ||
    !response.chatHistory ||
    response.chatHistory.length === 0
  ) {
    return [];
  }

  const messages: ChatMessage[] = [];
  let currentUserQuestion: string | undefined;

  // Iterate through history and pair user/assistant messages
  for (const historyMessage of response.chatHistory) {
    if (historyMessage.role === 'user') {
      // Store user question for next assistant message
      currentUserQuestion = historyMessage.content;
    } else if (historyMessage.role === 'assistant') {
      try {
        // Parse the JSON string content
        const parsedContent: ParsedAssistantContent = JSON.parse(
          historyMessage.content
        );

        // Build sources array from results, removing duplicates
        // A source is considered duplicate if it has the same file, page, and snippet
        const sourceMap = new Map<string, RagSourceReference>();

        // Process all results to build unique sources
        const allResults = parsedContent.results || [];
        if (parsedContent.recommended_value) {
          allResults.push(parsedContent.recommended_value);
        }

        for (const item of allResults) {
          const documentId = item.source || item.filename || '';
          const pageNumber = item.page;
          const snippet = item.snippet || '';

          // Create a unique key based on file, page, and snippet
          const sourceKey = `${documentId}|${
            pageNumber ?? 'no-page'
          }|${snippet}`;

          // Only add if we haven't seen this exact source before
          if (!sourceMap.has(sourceKey)) {
            sourceMap.set(sourceKey, {
              documentId,
              pageNumber,
              snippet,
              confidence: item.confidence_score,
              metadata: {
                fileName: item.filename,
                source: item.source,
                ...(item.page && { page: item.page }),
              },
            });
          }
        }

        const sources: RagSourceReference[] = Array.from(sourceMap.values());

        // Transform all results to UI type by adding the field property from user's question
        // Put recommended_value first if it exists, then all other results
        const fieldName = currentUserQuestion || 'Extracted field';
        const extractionResults = buildExtractionResults(
          parsedContent.recommended_value,
          parsedContent.results,
          fieldName
        );

        // Use array if multiple values, single item if only one
        // For empty results, create a placeholder to preserve the message in chat history
        const extractionData: ExtractionResultItem | ExtractionResultItem[] =
          extractionResults.length === 1
            ? extractionResults[0]
            : extractionResults.length > 1
            ? extractionResults
            : {
                // No results placeholder - preserves the question in chat history
                // Shows "no results" message when recommended_value is null
                field: fieldName,
                raw_value: null,
                formatted_value: null,
                confidence_score: 0,
                source: '',
                snippet: '',
                noResults: true, // Flag to indicate no results were found
              };

        // Citations are the same as sources
        const citations: RagSourceReference[] = sources;

        // Create ChatWithDocumentResponse structure
        // Use summary if available, otherwise use the extracted value
        const primaryExtractionForText = Array.isArray(extractionData)
          ? extractionData[0]
          : extractionData;
        const responseText =
          parsedContent.recommended_value?.summary ||
          parsedContent.results?.[0]?.summary ||
          (primaryExtractionForText
            ? String(
                primaryExtractionForText.formatted_value ??
                  primaryExtractionForText.raw_value ??
                  ''
              )
            : '');

        const chatResponse: ChatWithDocumentResponse = {
          response: responseText,
          citations,
          conversationId: response.conversationId || '',
        };

        // Create ChatMessage with the paired user question
        const chatMessage: ChatMessage = {
          question: currentUserQuestion || 'Previous question',
          response: chatResponse,
          timestamp: new Date(), // History doesn't include timestamps, use current time
          extractionData,
        };

        messages.push(chatMessage);
      } catch (error) {
        // Skip malformed messages
      }

      // Clear current question after pairing
      currentUserQuestion = undefined;
    }
  }

  return messages;
}
